import React, { useEffect, useState } from "react";
import Stars from "components/stars";
import Link from "helpers/link";
import Image from "components/image";
import Loading from "components/loading";
import Money from "components/money";
import { getProducts } from "services/storefront/common";

const Container = ({ layoutOrder, bgImage, title, description, ctaButton, children, className }) => (
    <div className={`flex container max-xsm:p-0 relative ${className}`}>
        <div className="absolute md:h-4/5 h-[70%] xsm:pr-4">
            <Image className="bg-overlay h-full rounded-md before:bg-black before:opacity-50" image={bgImage.asset} />
        </div>
        <div
            className={`${layoutOrder ? "lg:flex-row lg:justify-between" : "lg:flex-row-reverse lg:justify-end"} flex flex-col lg:gap-14 lg:flex-row lg:px-8 z-10 w-full items-center`}
        >
            <div className={`${layoutOrder && "lg:pl-4"} flex text-white lg:w-fit flex-col items-center lg:items-start px-2.5 lg:px-0 py-8 sm:py-9 md:py-20 lg:p-0`}>
                <h2 className="mb-2 font-semibold"> 
                    {title}
                </h2>
                <p className="lg:max-w-md text-center lg:text-left mb-4">
                    {description}
                </p>
                <Link to={ctaButton.url} className="btn-primary w-28 xsm:w-36 md:w-64 text-xs xsm:text-base py-2 md:py-3 font-bold text-black">
                    Shop Now
                </Link>
            </div>
            <div className="flex flex-nowrap lg:overflow-auto overflow-x-scroll w-full lg:w-9/12 xl:w-1/2">
                <div className="flex lg:flex-wrap gap-2 lg:gap-0 justify-between mx-1 lg:ml-0 mb-2">
                    {children}
                </div>
            </div>
        </div>
    </div>
);

const FeaturedProduct = ({ product, collectionHandle, collectionName }) => {
    const { title, variants } = product;
    const { image, compareAtPrice, price, title: variantTitle } = variants.edges[0].node;

    return (
        <Link to={collectionHandle} className="flex flex-col min-w-[15rem] lg:w-[48%] lg:min-w-0 w-full bg-white lg:max-w-xs ml-1 lg:mr-0 p-4 rounded-md shadow-gray mt-2 xsm:mt-6">
            <img src={image.url?.url || image.url} alt={`${title} - ${variantTitle}`} className="mb-4 lg:mb-8" />
            <Link to={collectionHandle} className="font-semibold text-gray-601 mb-4"> 
                {title}
            </Link>
            <div className="flex flex-col gap-1 text-gray-701 mb-2">
                <Stars rating={5} className="justify-start gap-0" />
                <div className="flex gap-2">
                    {(+compareAtPrice.amount - +price.amount) > 0 && (
                        <p className="line-through text-[#232020] font-normal"> 
                            <Money value={{ currencyCode: "USD", amount: +compareAtPrice.amount }} />
                        </p>
                    )}
                    <p className="text-[#232020] font-bold"> 
                        <Money value={{ currencyCode: "USD", amount: +price.amount }} />
                    </p>
                </div>
            </div>
            <Link to={collectionHandle} className="btn-primary w-full mt-auto font-bold text-black">
                {collectionName || "Shop Now"}
            </Link>
        </Link>
    );
};

const FeaturedCollection = ({ data }) => {
    const { bgImage, ctaButton, description, layoutOrder, title, items } = data;
    const [products, setProducts] = useState([]);
    const [error, setError] = useState("");

    const getCollections = async () => {
        try {
            const allProducts = await getProducts(items.map((item) => item.handle));

            setProducts(allProducts);
        } catch (err) {
            setError(err.message);
        }
    };

    useEffect(() => {
        getCollections();
    }, []);

    if (!products.length) {
        return (
            <Container
                bgImage={bgImage}
                title={title}
                ctaButton={ctaButton}
                description={description}
                layoutOrder={layoutOrder}
                className="h-[900px]"
            >
                <Loading className="justify-center" />                    
            </Container>
        );
    }

    if (error) {
        return (
            <div className="flex container max-xsm:p-0 relative">
                <h2 className="mb-2 font-semibold">
                    Something went wrong, please try again ...
                </h2>
            </div>
        );
    }

    return (
        <Container
            bgImage={bgImage}
            title={title}
            ctaButton={ctaButton}
            description={description}
            layoutOrder={layoutOrder}
        >
            {products?.map((product, index) => (
                <FeaturedProduct
                    key={product.id}
                    product={product}
                    collectionHandle={items[index]?.collectionHandle}
                    collectionName={items[index]?.collectionName}
                />
            ))}
        </Container>
    );
};

export default FeaturedCollection;
